import { css, cx } from "@emotion/css";
import type { ChipClassKey, ChipProps as MaterialChipProps } from "@material-ui/core/Chip";
import MaterialChip from "@material-ui/core/Chip";
import type { ClassNameMap } from "@material-ui/styles/withStyles/withStyles";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ReactNode } from "react";
import * as React from "react";
import { Tooltip } from "../Tooltip";
type ChipStyleProps = {
    backgroundColor?: string;
    labelColor?: string;
    markAsRemoved?: boolean;
    borderColor?: string;
    fullWidth?: boolean;
    noMargin?: boolean;
    accessibilityRole?: string;
    backgroundColorOnHover?: string;
    tooltipContent?: ReactNode;
    opacity?: number;
};
type ExposedMaterialChipProps = Pick<MaterialChipProps, "onClick" | "onDelete" | "deleteIcon" | "label" | "variant" | "tabIndex" | "icon" | "className">;
type ChipProps = ChipStyleProps & ExposedMaterialChipProps;
export function Chip(props: ChipProps) {
    const rootMarginClass = props.noMargin ? chipStyles.rootNoMargin : chipStyles.rootMargin;
    const classes: Partial<ClassNameMap<ChipClassKey>> = {
        root: cx(chipStyles.root, rootMarginClass),
        label: labelStyles.root,
    };
    const rootBackgroundColorStyle = props.backgroundColor ? { backgroundColor: props.backgroundColor } : undefined;
    const rootLabelColorStyle = props.labelColor ? { color: props.labelColor } : undefined;
    const rootOpacityStyle = props.opacity ? { opacity: props.opacity } : undefined;
    const rootStyleProps = rootBackgroundColorStyle || rootLabelColorStyle || rootOpacityStyle
        ? {
            style: {
                ...rootBackgroundColorStyle,
                ...rootLabelColorStyle,
                ...rootOpacityStyle,
            },
        }
        : {};
    //We can't just pass role through as undefined as it's a primitive aria attribute which will end up overriding anything material-ui provides
    //we therefore omit this prop when it hasn't been specified to allow the role to be changed if needed.
    const ariaProps = props.accessibilityRole ? { role: props.accessibilityRole } : {};
    const styledLabel = <span className={cx(labelStyles.text, { [labelWithLineThroughStyles]: props.markAsRemoved }, { [labelWithLimitedWidthStyles]: !props.fullWidth })}>{props.label}</span>;
    // The tooltip injects a div into this chip which can affect positioning of the label.
    // Choosing "flex" as the display (or any non-block/inline display) for this div helps achieve proper sizing, vertical centering and overflow for the label.
    // This aligns with the flex on labelStyles.root when there is no tooltip.
    const labelWithTooltip = props.tooltipContent ? (<Tooltip content={props.tooltipContent} display="flex">
            {styledLabel}
        </Tooltip>) : (styledLabel);
    return (<MaterialChip classes={classes} icon={props.icon} label={labelWithTooltip} onClick={props.onClick} tabIndex={props.tabIndex} variant={props.variant} onDelete={props.onDelete} deleteIcon={props.deleteIcon} {...rootStyleProps} {...ariaProps}/>);
}
const chipStyles = {
    root: css({
        "&.MuiChip-root": {
            lineHeight: "1.5rem",
            height: "1.5rem", // Specify height explicitly, or svg icons can push the container height out (eg. if you display
            // multiple chips in a row where some do/don't contain an avatar. E.g. environments list).
            maxWidth: "100%",
            display: "inline-flex",
            backgroundColor: themeTokens.color.portal.chip.background,
            verticalAlign: "middle",
            // This color affects the text color of the label through inheritance
            // We have to set it here so that it can be overridden through the style prop on the _root_ chip element from material-ui
            // Since this is the only element that we can affect using the style prop
            opacity: 1,
            "&:focus-within": {
                boxShadow: themeTokens.shadow.focused,
            },
        },
    }),
    rootMargin: css({
        "&.MuiChip-root": {
            margin: space[4],
        },
    }),
    rootNoMargin: css({
        "&.MuiChip-root": {
            margin: 0,
        },
    }),
};
const labelStyles = {
    root: css({
        flex: 1,
        display: "flex",
    }),
    text: css({
        fontSize: "0.75rem",
        fontWeight: "normal",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
    }),
};
const labelWithLineThroughStyles = css({
    textDecoration: "line-through",
});
const labelWithLimitedWidthStyles = css({
    maxWidth: "100%",
});
